import React from 'react';
import { Button, RangeSlider, SnapDialog } from '@one-snap/sparkles';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PropTypes from 'prop-types';
import './PaymentPlanPageStyles.css';
import Footbar from './Footbar';
import CartAmountHeader from './CartAmountHeader';
import CongratsBox from './CongratsBox';

function PaymentPlanPage({
  isVisible,
  onClose,
  totalAmount,
  goBack,
  onContinue,
  isSubmitLoading,
  applicationId,
  paymentPlan
}) {
  const formatDate = (dateString) => {
    const date = new Date(dateString);

    const options = {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    };

    return new Intl.DateTimeFormat('en-US', options).format(date);
  };
  return (
    <SnapDialog
      open={isVisible}
      onClose={onClose}
      className="custom-dialog-sm-width"
    >
      <div className="verification-page-div">
        <CartAmountHeader
          totalAmount={totalAmount}
          goBack={goBack}
          showCartAmount={false}
        />
        <br />
        <CongratsBox
          amount="5,000"
          applicationId={applicationId}
          approvalExpires={formatDate(
            paymentPlan.data.paymentPlanSummary.cpoDate
          )}
        />
        <br />
        <br />
        <br />
        <div className="awareness-content">
          <div className="lto-terms-checkboxes">
            <div className="lto-terms-internal-checkboxes">
              <div className="shopping-now-div">Shopping now?</div>
              <div className="shopping-now-div-content">
                Select your items at &lt;merchant&gt; and begin to check out.
                Tell Snap how much of your lease-to-own approval amount you want
                to use by providing transaction details. Present your single-use
                virtual card (active for 24 hours) to pay.
              </div>
              <div className="shopping-now-div-subcontent">
                &lt;2828 Buford Dr Ste 100-200, Buford, GA 30519&gt;
              </div>
              <div className="shopping-now-div-subcontent">
                &lt;(555) 867-5309&gt;
              </div>
              <br />
              <br />
              <div className="shopping-now-div">Shopping later?</div>
              <div className="shopping-now-div-content">
                Check your phone or email for a message with next steps when
                you&apos;re ready. You can use all or part of your approved
                amount for checkout when you&apos;re ready.
              </div>
            </div>
          </div>
          <br />
          <br />
          <br />
          <br />
          <div className="shopping-now-div">
            What could your payments look like?
          </div>
          <div className="shopping-now-div-content">
            Adjust the slider to estimate your payment based on the amount you
            may use.
          </div>
          <div className="range-slider-div">
            <div
              className="transaction-amount-div"
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                width: '100%'
              }}
            >
              <div className="transaction-label">Your transaction amount</div>
              <span className="transaction-amount">
                ${paymentPlan.data.paymentPlanSummary.leaseAmount}
              </span>
            </div>
            <br />
            <br />
            <div style={{ width: '100%' }}>
              <RangeSlider
                min={0}
                max={5000}
                value={Number(paymentPlan.data.paymentPlanSummary.leaseAmount)}
                style={{ width: '100%' }}
                disabled
              />
            </div>
            <div
              className="estimated-payments-title"
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                width: '100%',
                marginBottom: '0px'
              }}
            >
              <div>
                ${paymentPlan.data.paymentPlanSummary.finalNumberOfPayments}{' '}
                estimated payments of
              </div>
              <span className="estimated-payments-amount-title">
                ${paymentPlan.data.paymentPlanSummary.cpoPaymentsAmount}
              </span>
            </div>
            <div
              className="estimated-payments-subtitle"
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                width: '100%',
                marginBottom: '0px'
              }}
            >
              <div>Based on the info you have entered</div>
              <span className="estimated-payments-amount-subtitle">Weekly</span>
            </div>
          </div>
          <br />
          <br />
          <div className="questions-div">
            Have questions? Check out our FAQs.
          </div>
          <br />
          <div className="questions-div">
            <Accordion
              style={{
                borderTop: 'none',
                borderLeft: 'none',
                borderRight: 'none',
                boxShadow: 'none'
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon style={{ color: '#3D5CCF' }} />}
                aria-controls="panel1a-content"
                id="panel1a-header"
              >
                <div className="payment-que">
                  How does the Maximum Term Plan work?
                </div>
              </AccordionSummary>
              <AccordionDetails>
                <div className="payment-ans">
                  The maximum-term plan is the lowest payment option.
                </div>
                <div className="payment-ans payment-ans-margin">
                  Make regular lease payments until the end of your 12–18 month
                  maximum term, at which time you own the merchandise. This is
                  the default option unless you choose one of our early
                  ownership options. Keep in mind that with this plan, you may
                  pay more than twice the price of the retail goods, plus
                  applicable taxes and fees.
                </div>
                <div className="payment-ans payment-ans-margin">
                  Your lease payments will be automatically deducted from the
                  checking account provided in the lease application.
                </div>
              </AccordionDetails>
            </Accordion>
            <Accordion
              style={{
                borderTop: 'none',
                borderLeft: 'none',
                borderRight: 'none',
                boxShadow: 'none'
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon style={{ color: '#3D5CCF' }} />}
                aria-controls="panel2a-content"
                id="panel2a-header"
              >
                <div className="payment-que">
                  How does the &lt;100-Day Option&gt; work?
                </div>
              </AccordionSummary>
              <AccordionDetails>
                <div className="payment-ans">
                  The 100-Day Option is the lowest cost option.
                </div>
                <div className="payment-ans payment-ans-margin">
                  Reduce your cost of lease and acquire early ownership by
                  making payments equal to the cash price of the merchandise,
                  plus sales tax, processing fees, and any applicable cost of
                  lease within &lt;100 days&gt; after you sign your agreement.
                  See your agreement for specific details regarding your lease.
                  Contact us to take advantage of this option.
                </div>
              </AccordionDetails>
            </Accordion>
            <Accordion
              style={{
                borderTop: 'none',
                borderLeft: 'none',
                borderRight: 'none',
                boxShadow: 'none'
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon style={{ color: '#3D5CCF' }} />}
                aria-controls="panel2a-content"
                id="panel2a-header"
              >
                <div className="payment-que">
                  How does the Early Buyout Option work?
                </div>
              </AccordionSummary>
              <AccordionDetails>
                <div className="payment-ans">
                  The Early Buyout Option has the most flexible lease term.
                </div>
                <div className="payment-ans payment-ans-margin">
                  At any time, you can acquire ownership of the merchandise by
                  paying all full-term lease payments that are not yet due and
                  receive a discount on those lease payments. Contact us to take
                  advantage of this option.
                </div>
              </AccordionDetails>
            </Accordion>
            <Divider />
          </div>
          <br />
          <br />
          <br />
          <Button
            className="continue-button"
            onClick={onContinue}
            loading={isSubmitLoading}
          >
            Sign Agreement
          </Button>
          <Footbar />
        </div>
      </div>
    </SnapDialog>
  );
}

PaymentPlanPage.propTypes = {
  isVisible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  goBack: PropTypes.func.isRequired,
  totalAmount: PropTypes.string.isRequired,
  onContinue: PropTypes.func.isRequired,
  isSubmitLoading: PropTypes.bool.isRequired,
  applicationId: PropTypes.string.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  paymentPlan: PropTypes.object.isRequired
};

export default PaymentPlanPage;
